import {Box} from '@mui/material';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {FC, Fragment, useState} from 'react';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {InvoicesTableProps} from './InvoicesTable.types';
import {columnConfigInvoices} from './InvoicesTable.tableConfig';
import {InvoiceSortEnum} from 'src/enums';
import {InvoicesTableActions} from '../InvoicesTableActions';
import {InvoiceDTO, InvoiceSearchRequestData, RevenueInvoiceDTO} from 'src/types';
import {InvoiceTableCard} from './InvoiceTableCard';
import {InvoicesFilterValue} from '../InvoicesTableActions/InvoicesTableActions.types';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';

export const InvoicesTable: FC<InvoicesTableProps> = ({
                                                          size,
                                                          data,
                                                          filters,
                                                          handleUpdateTableData,
                                                          handleChangePage,
                                                          handleChangeRowsNumber,
                                                          title,
                                                          onAddClick,
                                                          buttonText,
                                                          onlyTitle,
                                                          icon,
                                                          revenue = false,
                                                      }) => {
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from an
    // invoice is, and starting at '' meant the next filter change wiped the search that was applied.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({
        field: filters.sortField ?? InvoiceSortEnum.CREATED,
        sort: filters.sortAsc ? 'asc' : 'desc',
    });

    /**
     * The filter the list is asked for: a merge onto what it is already on, not a fresh object.
     *
     * It used to be built from scratch out of the toolbar's own two fields, so every keystroke in the
     * search box replaced the whole filter - and with it the status the strip's tiles had set. The
     * page holds this state and passes it in; nothing here keeps a second copy of it.
     */
    const updateData = (
        search: string,
        sort: GridSortItem,
        filter: InvoiceSearchRequestData
    ): InvoiceSearchRequestData => ({
        ...filter,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : true,
        sortField: (sort && sort.field) || InvoiceSortEnum.CREATED,
    });

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    const handleFilter = (value: InvoicesFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {...filters, ...value}));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    const isRevenueInvoice = (item: InvoiceDTO | RevenueInvoiceDTO): item is RevenueInvoiceDTO => {
        return !!(item as RevenueInvoiceDTO)?.business;
    };

    return (
        <Box>
            <TableLayout
                toolbar={(
                    <InvoicesTableActions
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        revenue={revenue}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        status={String(filters.status ?? '')}
                        businessId={filters.businessId ?? 0}
                        searchValue={searchValue}
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={data.page.number}
                pageSize={size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
            <ResponsiveTable
                desktopElement={
                    <Table
                        rows={data.content}
                        hideFooter
                        columns={columnConfigInvoices(revenue)}
                        handleSort={handleSort}
                    />
                }
                responsiveElement={
                    <CardList>
                        {data?.content?.map((item) => (
                            <Fragment key={item?.id}>
                                {isRevenueInvoice(item) && (
                                    <InvoiceTableCard
                                        key={item?.id}
                                        revenue={revenue}
                                        adminData={item}
                                    />
                                )}
                                {!isRevenueInvoice(item) && (
                                    <InvoiceTableCard
                                        key={item?.id}
                                        revenue={revenue}
                                        businessData={item}
                                    />
                                )}
                            </Fragment>
                        ))}
                    </CardList>
                }
            />
            </TableLayout>
        </Box>
    );
};
